/**
 * Where Tab and the arrow keys go next in an editable grid.
 *
 * Kept apart from the component because this is the whole of the behaviour:
 * which cell Tab lands on, given what is editable. Read-only columns are
 * stepped over rather than stopped on — Tab is for reaching the next thing you
 * can type into, not for touring the row.
 */

export interface GridCellPosition {
  rowIndex: number
  colIndex: number
}

interface FindArgs {
  from: GridCellPosition
  /** 1 for Tab, -1 for Shift+Tab. */
  step: 1 | -1
  rowCount: number
  colCount: number
  isEditable: (rowIndex: number, colIndex: number) => boolean
}

/**
 * Returns the next editable cell, carrying on to the following row when the
 * current one has none left, or `null` at either end of the grid — which is the
 * caller's cue to let the browser have the Tab so focus can leave the grid.
 */
export const findNextEditableCell = ({
  from,
  step,
  rowCount,
  colCount,
  isEditable,
}: FindArgs): GridCellPosition | null => {
  if (rowCount <= 0 || colCount <= 0) return null

  let rowIndex = from.rowIndex
  let colIndex = from.colIndex + step

  while (rowIndex >= 0 && rowIndex < rowCount) {
    if (colIndex < 0) {
      rowIndex -= 1
      colIndex = colCount - 1
      continue
    }
    if (colIndex >= colCount) {
      rowIndex += 1
      colIndex = 0
      continue
    }
    if (isEditable(rowIndex, colIndex)) return { rowIndex, colIndex }
    colIndex += step
  }

  return null
}

export type GridDirection = 'up' | 'down' | 'left' | 'right'

interface FindDirectionArgs {
  from: GridCellPosition
  direction: GridDirection
  rowCount: number
  colCount: number
  isEditable: (rowIndex: number, colIndex: number) => boolean
}

/**
 * The nearest editable cell in one direction — the arrow keys' answer.
 *
 * Unlike Tab this never wraps: up and down stay in their column, left and
 * right stay in their row. An arrow key is a statement about direction, and a
 * grid that jumped to the far end of the row above in answer to it would lose
 * the user their place. Read-only columns in between are stepped over, so the
 * arrows walk the fields you can type into rather than stopping on labels.
 */
export const findEditableCellInDirection = ({
  from,
  direction,
  rowCount,
  colCount,
  isEditable,
}: FindDirectionArgs): GridCellPosition | null => {
  if (rowCount <= 0 || colCount <= 0) return null

  const rowStep = direction === 'down' ? 1 : direction === 'up' ? -1 : 0
  const colStep = direction === 'right' ? 1 : direction === 'left' ? -1 : 0

  let { rowIndex, colIndex } = from

  for (;;) {
    rowIndex += rowStep
    colIndex += colStep

    if (rowIndex < 0 || rowIndex >= rowCount) return null
    if (colIndex < 0 || colIndex >= colCount) return null

    if (isEditable(rowIndex, colIndex)) return { rowIndex, colIndex }
  }
}
